import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type SiteSetting = {
  key: string;
  value: string;
  updated_at: string;
};

export const SITE_SETTING_DEFAULTS: Record<string, string> = {
  home_intro_title: 'Danmarks åbne elbilkatalog',
  launch_campaign_enabled: 'false',
};

export function useSiteSetting(key: string) {
  return useQuery({
    queryKey: ['site_setting', key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value, updated_at')
        .eq('key', key)
        .maybeSingle();
      if (error) throw error;
      return (data?.value ?? SITE_SETTING_DEFAULTS[key] ?? '') as string;
    },
    staleTime: 60_000,
    refetchInterval: key === 'launch_campaign_enabled' ? 30_000 : false,
  });
}

export function useAllSiteSettings() {
  return useQuery({
    queryKey: ['site_settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value, updated_at')
        .order('key');
      if (error) throw error;
      return (data ?? []) as SiteSetting[];
    },
  });
}

export function useUpdateSiteSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from('site_settings')
        .upsert({ key, value }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['site_settings'] });
      qc.invalidateQueries({ queryKey: ['site_setting', vars.key] });
    },
  });
}
